import type { CareStateDomainDb, ClinicalScopeT, PatientLinkT } from '../model/types';

import { isVisibleInScope, resolveProblemIdForRequest } from '../lib/visibility';

function patientLink(): PatientLinkT {
  return { type: 'patient' } as const;
}

/**
 * Return a scoped view of CareState, per ClinicalScope rules.
 *
 * - Linked tables (`requests`, `internal_notes`, `follow_ups`, `lab_orders`, `prescriptions`, `proposed_actions`)
 *   are filtered via `isVisibleInScope`.
 * - `patients` are always visible in all scopes by the documented rules.
 * - `problems` don't have a link field, so we model them as `ProblemLinkT(problem.id)` for visibility.
 */
export function selectScopedCareState(
  db: CareStateDomainDb,
  scope: ClinicalScopeT
): CareStateDomainDb {
  const scopedProblems = (() => {
    switch (scope.type) {
      case 'patient':
        return db.problems;
      case 'problem':
        return db.problems.filter((p) => p.id === scope.id);
      case 'request': {
        const problemId = resolveProblemIdForRequest(scope.id, db);
        if (problemId === null) return [];
        return db.problems.filter((p) => p.id === problemId);
      }
      default:
        return [];
    }
  })();

  const scopedRequests = db.requests.filter((r) => isVisibleInScope(scope, r.link, db));

  const scopedInternalNotes = db.internal_notes.filter((n) => isVisibleInScope(scope, n.link, db));

  const scopedFollowUps = db.follow_ups.filter((f) => isVisibleInScope(scope, f.link, db));

  const scopedLabOrders = db.lab_orders.filter((l) => isVisibleInScope(scope, l.link, db));

  const scopedPrescriptions = db.prescriptions.filter((p) => isVisibleInScope(scope, p.link, db));

  const scopedProposedActions = db.proposed_actions.filter((a) =>
    isVisibleInScope(scope, a.link, db)
  );

  const scopedPriorAuthRequests = db.prior_auth_requests.filter((r) =>
    isVisibleInScope(scope, r.link, db)
  );

  const scopedPatients = db.patients.filter(() => isVisibleInScope(scope, patientLink(), db));

  return {
    patients: scopedPatients,
    problems: scopedProblems,
    requests: scopedRequests,
    internal_notes: scopedInternalNotes,
    follow_ups: scopedFollowUps,
    lab_orders: scopedLabOrders,
    prescriptions: scopedPrescriptions,
    pre_requests: db.pre_requests,
    proposed_actions: scopedProposedActions,
    prior_auth_requests: scopedPriorAuthRequests,
  };
}
